import { Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { EditLockEntity, EditLockStatus } from '../../../models/edit-lock.model';
import { EditLockService } from '../../../services/edit-lock.service';

export type EditLockBarState = 'loading' | 'view' | 'editing' | 'locked';

/**
 * Edit mode for one user or business. The page opens view-only; Edit takes the entity's lease
 * so nobody else can edit it, and the bar keeps the lease alive while the page stays in edit
 * mode. Done, leaving the page, closing the tab or ten minutes without activity give it back.
 * If someone else is editing, the bar says who and Edit stays unavailable until they finish.
 *
 * The host binds (editingChange) and disables its edit controls while it is false; the API
 * refuses writes from anyone not holding the lease regardless.
 */
@Component({
  selector: 'app-edit-lock-bar',
  standalone: false,
  templateUrl: './edit-lock-bar.component.html',
  styleUrls: ['./edit-lock-bar.component.css']
})
export class EditLockBarComponent implements OnChanges, OnDestroy {
  @Input() entityType!: EditLockEntity;
  @Input() entityId: string | null | undefined;
  /** What is being edited, for the messages: "this user", "this business". */
  @Input() noun = 'record';
  @Output() editingChange = new EventEmitter<boolean>();

  /** Leaving edit mode after this long without a click or keystroke frees the entity for others. */
  static readonly IdleLimitMs = 10 * 60 * 1000;
  /** How often a view-only page re-checks who is editing. */
  static readonly PollMs = 30 * 1000;

  state: EditLockBarState = 'loading';
  holderName: string | null = null;
  since: string | null = null;
  busy = false;
  notice = '';

  /** The lease this bar holds, kept apart from the inputs so it is released even after they change. */
  private held: { entityType: EditLockEntity; entityId: string } | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private lastActivity = Date.now();

  constructor(private editLocks: EditLockService) { }

  get editing(): boolean {
    return this.state === 'editing';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['entityId'] && !changes['entityType']) return;

    // A different user or business: whatever was held belongs to the previous one.
    const wasEditing = this.editing;
    this.stopHeartbeat();
    this.releaseHeld();
    this.notice = '';
    this.state = 'loading';
    if (wasEditing) {
      // Not synchronously: the host is mid change detection.
      setTimeout(() => this.editingChange.emit(false));
    }

    this.refreshStatus();
    this.startPolling();
  }

  ngOnDestroy(): void {
    this.stopHeartbeat();
    this.stopPolling();
    this.releaseHeld();
  }

  @HostListener('document:mousedown')
  @HostListener('document:keydown')
  @HostListener('document:touchstart')
  onActivity(): void {
    this.lastActivity = Date.now();
  }

  /** Tab closed or reloaded: an ordinary request would be cancelled, so hand the lease back with keepalive. */
  @HostListener('window:pagehide')
  onPageHide(): void {
    if (this.held) {
      this.editLocks.releaseOnUnload(this.held.entityType, this.held.entityId);
      this.held = null;
    }
  }

  startEdit(): void {
    const entityId = this.entityId;
    if (!entityId || this.busy) return;
    const entityType = this.entityType;

    this.busy = true;
    this.notice = '';
    this.editLocks.acquire(entityType, entityId).subscribe({
      next: status => {
        if (!this.isCurrent(entityType, entityId)) {
          // The page moved on while this was in flight; give back what was just taken.
          if (status.heldByMe) this.editLocks.release(entityType, entityId).subscribe({ error: () => { } });
          return;
        }
        if (status.heldByMe) {
          this.enterEditing(entityType, entityId, status);
        } else {
          this.showStatus(status);
        }
      },
      error: (error: HttpErrorResponse) => {
        this.notice = error?.error?.message || 'Could not switch to edit mode. Try again.';
      }
    }).add(() => (this.busy = false));
  }

  done(): void {
    this.leaveEditing();
  }

  private enterEditing(entityType: EditLockEntity, entityId: string, status: EditLockStatus): void {
    this.held = { entityType, entityId };
    this.state = 'editing';
    this.holderName = null;
    this.since = null;
    this.lastActivity = Date.now();
    this.stopPolling();
    this.startHeartbeat(status.leaseSeconds);
    this.editingChange.emit(true);
  }

  private leaveEditing(notice = ''): void {
    const wasEditing = this.editing;
    this.stopHeartbeat();
    this.releaseHeld();
    this.state = 'view';
    this.notice = notice;
    if (wasEditing) this.editingChange.emit(false);
    this.refreshStatus();
    this.startPolling();
  }

  /** Renew well inside the lease, so one slow or missed beat does not lose it. */
  private startHeartbeat(leaseSeconds: number): void {
    this.stopHeartbeat();
    const everyMs = Math.max(10, Math.floor((leaseSeconds || 120) / 3)) * 1000;
    this.heartbeatTimer = setInterval(() => this.beat(), everyMs);
  }

  private beat(): void {
    const held = this.held;
    if (!held) return;

    if (Date.now() - this.lastActivity > EditLockBarComponent.IdleLimitMs) {
      this.leaveEditing('Edit mode ended after 10 minutes without activity, so others can edit.');
      return;
    }

    this.editLocks.acquire(held.entityType, held.entityId).subscribe({
      next: status => {
        if (this.held !== held || status.heldByMe) return;
        // Lost it - e.g. the computer slept past the lease and someone else took over.
        this.stopHeartbeat();
        this.held = null;
        this.editingChange.emit(false);
        this.showStatus(status);
        this.notice = 'Your edit session ended, so this page is view-only again. Anything not yet saved was not sent.';
        this.startPolling();
      },
      error: () => {
        // A missed beat is survivable: the lease outlasts two more.
      }
    });
  }

  private refreshStatus(): void {
    const entityId = this.entityId;
    if (!entityId) {
      this.state = 'view';
      return;
    }
    const entityType = this.entityType;
    this.editLocks.status(entityType, entityId).subscribe({
      next: status => {
        if (!this.isCurrent(entityType, entityId) || this.editing) return;
        this.showStatus(status);
      },
      error: () => {
        if (this.isCurrent(entityType, entityId) && !this.editing) this.state = 'view';
      }
    });
  }

  private showStatus(status: EditLockStatus): void {
    if (status.locked) {
      this.state = 'locked';
      this.holderName = status.holderName;
      this.since = status.acquiredAtUtc;
    } else {
      this.state = 'view';
      this.holderName = null;
      this.since = null;
    }
  }

  private startPolling(): void {
    this.stopPolling();
    this.pollTimer = setInterval(() => {
      if (!this.editing && !this.busy) this.refreshStatus();
    }, EditLockBarComponent.PollMs);
  }

  private stopPolling(): void {
    if (this.pollTimer) clearInterval(this.pollTimer);
    this.pollTimer = null;
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = null;
  }

  private releaseHeld(): void {
    const held = this.held;
    this.held = null;
    if (held) {
      this.editLocks.release(held.entityType, held.entityId).subscribe({ error: () => { /* lapses anyway */ } });
    }
  }

  private isCurrent(entityType: EditLockEntity, entityId: string): boolean {
    return this.entityType === entityType && this.entityId === entityId;
  }
}
